

import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import useHttp from '../../../../hook/use-http';
import { API_URL_MAIN } from '../../../../constants/driveglobalfilterinitalstates';
import styles from './GrowthTrackingTab.module.css';
import moment from 'moment';


const EnergyEfficiencyTrends = ({ filterdata }) => {
  const { sendReq: fetchData } = useHttp();
  const [chartView, setChartView] = useState('Monthly Graph');
  const [data, setData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(moment().subtract(1, 'days'));

  const getDateRange = (monthMoment) => {
    return {
      startDate: monthMoment.clone().startOf('month').format('YYYY-MM-DD'),
      endDate: monthMoment.clone().endOf('month').format('YYYY-MM-DD'),
    };
  };

  const getCumulativeRange = () => {
    // const today = moment();
    const today = moment().subtract(1, 'days');
    const sixMonthsAgo = today.clone().subtract(5, 'months').startOf('month');
    return {
      startDate: sixMonthsAgo.format('YYYY-MM-DD'),
      endDate: today.clone().endOf('month').format('YYYY-MM-DD'),
    };
  };


  const fetchEnergyData = () => {
    if (!filterdata || Object.keys(filterdata).length === 0) return;

    let dateRange = {};
    let apiURL = '';

    if (chartView === 'Cumulative') {
      dateRange = getCumulativeRange();
      apiURL = API_URL_MAIN + 'growth_tracking/energy_efficiency_monthwise';
    } else if (chartView === 'Monthly Graph') {
      dateRange = getDateRange(selectedMonth);
      apiURL = API_URL_MAIN + 'growth_tracking/energy_efficiency_monthlygraph';
    }

    const requestBody = {
      filterdata: {
        ...filterdata,
        ...dateRange
      }
    };

    fetchData({ url: apiURL, body: requestBody }, (result) => {
      if (result) {
        if (chartView === 'Cumulative') {
          // directly use as-is for monthwise
          // if (Array.isArray(result)) {
          //   setData(result);
          // } else {
          //   setData([]);
          // }
          if (chartView === 'Cumulative') {
            if (Array.isArray(result)) {
              const transformed = result.map(item => ({
                month: moment(item.key.eventdate).format('MMMM YYYY'),
                targetEnergyEfficiency: item.Target_Energy_Efficiency?.value?.toFixed(3) ?? 0, // optional
                actualEnergyEfficiency: item.actual_Energy_Efficiency?.value?.toFixed(3) ?? 0,
              }));

              setData(transformed);
            } else {
              setData([]);
            }
          }
        }
        // else if (chartView === 'Monthly Graph') {
        //   // map buckets into required format for recharts
        //   if (result.groupby && Array.isArray(result.groupby.buckets)) {
        //     const transformed = result.groupby.buckets.map(bucket => ({
        //       month: bucket.key.day_wise, // here using 'month' key for XAxis compatibility
        //       targetEnergyEfficiency: bucket.Target_Energy_Efficiency.value?.toFixed(3) ?? 0,
        //       actualEnergyEfficiency: bucket.actual_Energy_Efficiency.value?.toFixed(3) ?? 0
        //     }));
        //     setData(transformed);
        //   } else {
        //     setData([]);
        //   }
        // }
        //       else if (chartView === 'Monthly Graph') {
        //   if (result.groupby && Array.isArray(result.groupby.buckets)) {
        //     // 👉 sort by day_wise first
        //     const sortedBuckets = result.groupby.buckets.sort(
        //       (a, b) => new Date(a.key.day_wise) - new Date(b.key.day_wise)
        //     );

        //     // then map into recharts-friendly format
        //     const transformed = sortedBuckets.map(bucket => ({
        //       month: bucket.key.day_wise,
        //       targetEnergyEfficiency: bucket.Target_Energy_Efficiency.value?.toFixed(3) ?? 0,
        //       actualEnergyEfficiency: bucket.actual_Energy_Efficiency.value?.toFixed(3) ?? 0
        //     }));

        //     setData(transformed);
        //   } else {
        //     setData([]);
        //   }
        // }
        else if (chartView === 'Monthly Graph') {
          if (result.groupby && Array.isArray(result.groupby.buckets)) {
            // 👉 sort by eventdate
            const sortedBuckets = result.groupby.buckets.sort(
              (a, b) => new Date(a.key.eventdate) - new Date(b.key.eventdate)
            );

            // then map into recharts-friendly format
            const transformed = sortedBuckets.map(bucket => ({
              month: moment(bucket.key.eventdate).format('DD MMM'),  // use eventdate here
              targetEnergyEfficiency: bucket.Target_Energy_Efficiency?.value?.toFixed(3) ?? 0,
              actualEnergyEfficiency: bucket.actual_Energy_Efficiency?.value?.toFixed(3) ?? 0
            }));

            setData(transformed);
          } else {
            setData([]);
          }
        }

      } else {
        setData([]);
      }
    });
  };


  // fetch when dependencies change
  useEffect(() => {
    fetchEnergyData();
  }, [chartView, filterdata, selectedMonth]);

  const goToPreviousMonth = () => {
    setSelectedMonth(prev => prev.clone().subtract(1, 'month'));
  };

  const goToNextMonth = () => {
    setSelectedMonth(prev => prev.clone().add(1, 'month'));
  };

  return (
    <div className={`${styles.dashboardContainer} p-3`}>


      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className={styles.sectionTitle}>Energy Efficiency Trends</h3>

        {/* 👇 Only show month selectors for Monthly Graph */}
        {chartView === 'Monthly Graph' && (
          <div className="d-flex align-items-center">
            <span onClick={goToPreviousMonth} className={`${styles.month}`}>
              <i className="fas fa-chevron-left"></i>
            </span>
            <div className="border p-2 mx-2 rounded">
              {selectedMonth.format('MMMM YYYY')}
            </div>
            <span onClick={goToNextMonth} className={`${styles.month}`}>
              <i className="fas fa-chevron-right"></i>
            </span>
          </div>
        )}

        <div className="d-flex" style={{ backgroundColor: '#f8f9fa', borderRadius: '25px', padding: '4px' }}>
          <button
            className={`${styles.btnToggle} ${chartView === 'Cumulative' ? styles.active : ''} me-1`}
            onClick={() => setChartView('Cumulative')}
            style={{
              backgroundColor: chartView === 'Cumulative' ? '#83a4cf' : 'transparent',
              color: chartView === 'Cumulative' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '20px',
              padding: '8px 20px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Cumulative
          </button>
          <button
            className={`${styles.btnToggle} ${chartView === 'Monthly Graph' ? styles.active : ''}`}
            onClick={() => setChartView('Monthly Graph')}
            style={{
              backgroundColor: chartView === 'Monthly Graph' ? '#83a4cf' : 'transparent',
              color: chartView === 'Monthly Graph' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '20px',
              padding: '8px 20px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Monthly Graph
          </button>
        </div>
      </div>

      {/* 👇 existing chart section as-is */}
      <div className={`${styles.chartContainer} p-3`}>
        {/* <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              interval={0}
              label={{
                value: 'Monthwise',
                position: 'insideBottom',
                offset: -30,
                style: { textAnchor: 'middle', fill: '#666', fontSize: '12px' }
              }}
            />
            <YAxis
              domain={['auto', 'auto']}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              label={{
                value: 'Energy Efficiency (kwH/Km)',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#666', fontSize: '12px' }
              }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '12px'
              }}
              formatter={(value, name) => [`${value} kwH/Hr`, name]}
              labelFormatter={() => ' '}
            />
            <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="line" align="right" verticalAlign="top" layout="vertical" />
            <Line type="monotone" dataKey="targetEnergyEfficiency" stroke="#17becf" strokeWidth={2}
              dot={{ fill: '#17becf', strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, stroke: '#17becf', strokeWidth: 2, fill: 'white' }}
              name="Benchmark"
            />
            <Line type="monotone" dataKey="actualEnergyEfficiency" stroke="#e377c2" strokeWidth={2}
              dot={{ fill: '#e377c2', strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, stroke: '#e377c2', strokeWidth: 2, fill: 'white' }}
              name="Energy Efficiency"
            />
          </LineChart>
           <div className="mb-3">
        <small className={`${styles.carbonInfo} fst-italic`}>
          Note: Cumulative will be for 6 months of data.
        </small>
      </div>
        </ResponsiveContainer> */}
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              interval={0}
              label={
                chartView === "Cumulative"
                  ? {
                    value: 'Monthwise',
                    position: 'insideBottom',
                    offset: -15,
                    style: {
                      textAnchor: 'middle',
                      fill: '#666',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }
                  }
                  : {
                    value: 'Day-Wise',
                    position: 'insideBottom',
                    offset: -15,
                    style: {
                      textAnchor: 'middle',
                      fill: '#666',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }
                  }
              }
            />
            <YAxis
              domain={['auto', 'auto']}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              label={{
                value: 'Energy Efficiency (kwH/Km)',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#666', fontSize: '12px' }
              }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '12px'
              }}
              formatter={(value, name) => [`${value} kwH/Hr`, name]}
              labelFormatter={(label) => label}
            />
            <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="line" align="right" verticalAlign="top" layout="vertical" />
            <Line type="monotone" dataKey="targetEnergyEfficiency" stroke="#17becf" strokeWidth={2}
              dot={{ fill: '#17becf', strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, stroke: '#17becf', strokeWidth: 2, fill: 'white' }}
              name="Benchmark"
            />
            <Line type="monotone" dataKey="actualEnergyEfficiency" stroke="#e377c2" strokeWidth={2}
              dot={{ fill: '#e377c2', strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, stroke: '#e377c2', strokeWidth: 2, fill: 'white' }}
              name="Energy Efficiency"
            />

            {/* ✅ Note inside chart container */}
            {chartView === "Cumulative" && (
              <text
                x="50%"
                y="95%"
                textAnchor="middle"
                fontSize="11"
                fill="#6c757d"
                fontStyle="italic"
              >
                Note: Cumulative will be for 6 months of data.
              </text>
            )}
          </LineChart>
        </ResponsiveContainer>

      </div>
    </div>
  );
};
export default EnergyEfficiencyTrends;
